import FileUpload from 'react-scale/dashboard.scale.com/components/SelfServe/Upload/FileUpload';
import logger from 'react-scale/lib/logging';
import { ProgressStepper, Spinner, Typography, Button } from '@scale/scaleui';
import FileSaver from 'file-saver';
import { useState, useCallback } from 'react';
import { ChatAudioTranscript } from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/audio-attachments/ChatAudioTranscript';
import { ChatAudioEditor } from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/audio-attachments/ChatAudioEditor';
import { getAudioSource } from 'react-scale/lib/chat-api/multimodal';
import {
  SelfServeSupportedAttachmentMimeTypes,
  SelfServeSupportedAttachmentTypes,
} from '@scale/shared/defaults';

export enum AudioUploadErrorMessage {
  FailedToCopy = 'Failed to copy',
  ErrorDownloadingFile = 'Error downloading file',
  ErrorUploadingFile = 'Error uploading file',
}

enum AudioUploadCopyStatus {
  CopyLinkToFile = 'Copy link to file',
  Copied = 'Copied!',
}

interface AudioUploadSectionProps {
  file: File | null;
  setFile: (file: File | null) => void;
  fileName: string | undefined;
  fileUrl: string | undefined;
  mimeType: string | undefined;
  disabled?: boolean;
  handleFileUpload: (
    file: File,
    setProgress: (progress: number | null) => void,
    setUploading: (uploading: boolean) => void,
    setFileUrl: (url: string | undefined) => void,
    setErrorMessage: (message: AudioUploadErrorMessage | undefined) => void,
  ) => void;
  handleCancel: () => void;
  handleUploadRetry: (
    file: File,
    setProgress: (progress: number | null) => void,
    setUploading: (uploading: boolean) => void,
    setFileUrl: (url: string | undefined) => void,
    setErrorMessage: (message: AudioUploadErrorMessage | undefined) => void,
  ) => Promise<void>;
  setFileUrl: (url: string | undefined) => void;
}

const AudioUploadSection = ({
  file,
  setFile,
  fileName,
  fileUrl,
  mimeType,
  disabled,
  handleFileUpload,
  handleCancel,
  setFileUrl,
  handleUploadRetry,
}: AudioUploadSectionProps) => {
  const [progress, setProgress] = useState<number | null>(null);
  const [copyStatus, setCopyStatus] = useState<AudioUploadCopyStatus>(
    AudioUploadCopyStatus.CopyLinkToFile,
  );
  const [uploading, setUploading] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<AudioUploadErrorMessage | undefined>(undefined);

  const handleCopyLink = async () => {
    try {
      if (fileUrl) {
        await navigator.clipboard.writeText(fileUrl);
        setCopyStatus(AudioUploadCopyStatus.Copied);
      }
    } catch (err) {
      setErrorMessage(AudioUploadErrorMessage.FailedToCopy);
    }
  };

  const handleAddFile = useCallback(
    (files: File[]) => {
      const file = files[0];
      setFile(file);
      handleFileUpload(file, setProgress, setUploading, setFileUrl, setErrorMessage);
    },
    [handleFileUpload, setFile, setProgress, setUploading, setFileUrl, setErrorMessage],
  );

  const handleFileDownload = useCallback(async () => {
    if (fileUrl) {
      setErrorMessage(undefined);
      setDownloading(true);
      try {
        const url = (await getAudioSource({ audioUrl: fileUrl })).source;

        const response = await fetch(
          url,
          mimeType
            ? {
                headers: { 'Content-Type': mimeType },
              }
            : undefined,
        );
        if (!response || !response.body) {
          logger.error('Failed to download file');
          return;
        }
        const reader = response.body.getReader();
        const stream = new ReadableStream({
          start(controller) {
            function push() {
              reader
                .read()
                .then(({ done, value }) => {
                  if (done) {
                    controller.close();
                    return; // Properly returning from the `then()`
                  }
                  controller.enqueue(value);
                  return push(); // Return the next call to push()
                })
                .catch(err => {
                  logger.error('Stream reading failed:', err);
                  controller.error(err); // Report any errors to the stream
                  throw err; // Ensure the catch block throws an error
                });
            }
            push();
          },
        });
        const blob = await new Response(stream).blob();
        FileSaver.saveAs(blob, fileName);
      } catch (e) {
        setErrorMessage(AudioUploadErrorMessage.ErrorDownloadingFile);
      }
      setDownloading(false);
    }
  }, [fileUrl, fileName, mimeType, setErrorMessage, setDownloading]);

  return (
    <>
      {fileUrl ? (
        <>
          <Typography.Paragraph2>Filename: {fileName}</Typography.Paragraph2>
          <div className="flex flex-row gap-2 items-center">
            <Button
              variant={copyStatus === AudioUploadCopyStatus.Copied ? 'success' : 'secondary'}
              onClick={handleCopyLink}
            >
              {copyStatus}
            </Button>
          </div>
        </>
      ) : file ? (
        <>{fileName && <Typography.Paragraph2>Filename: {fileName}</Typography.Paragraph2>}</>
      ) : (
        <FileUpload
          allowedMimeTypes={
            SelfServeSupportedAttachmentMimeTypes[SelfServeSupportedAttachmentTypes.Audio]
          }
          onFiles={handleAddFile}
        />
      )}
      {progress != null && (
        <div className="flex flex-col items-center">
          <Typography.Paragraph2 className="font-mono font-semibold">
            {Math.round(progress)}%
          </Typography.Paragraph2>
          <ProgressStepper size="sm" steps={100} stepsCompleted={progress} />
        </div>
      )}
      <div className="mt-2 flex flex-row gap-2 items-center">
        {!disabled && (fileUrl || errorMessage === AudioUploadErrorMessage.ErrorUploadingFile) && (
          <>
            <Button
              variant="danger"
              onClick={() => {
                handleCancel();
                setErrorMessage(undefined);
                setCopyStatus(AudioUploadCopyStatus.CopyLinkToFile);
              }}
            >
              Cancel
            </Button>
            {errorMessage === AudioUploadErrorMessage.ErrorUploadingFile && file && (
              <Button
                onClick={() =>
                  handleUploadRetry(file, setProgress, setUploading, setFileUrl, setErrorMessage)
                }
              >
                Retry Upload
              </Button>
            )}
            {uploading && <Spinner size="sm" />}
          </>
        )}
        {fileUrl && (
          <Button onClick={() => handleFileDownload()}>
            Download File
            {downloading && <Spinner size="sm" />}
          </Button>
        )}
      </div>
      {fileUrl && (
        <div className="flex flex-col gap-2">
          <ChatAudioEditor src={fileUrl} readonly={true} />
          <ChatAudioTranscript audioUrl={fileUrl} userGeneratedAudio={true} />
        </div>
      )}
      {errorMessage && <div>{errorMessage}</div>}
    </>
  );
};

export default AudioUploadSection;
